/**
 * Confirm-execute — the invariant part of "confirm these legs".
 *
 * Sync-then-Persist: every external (EAPI) trip is created first; only when
 * they all succeed do we write locally. If any one fails, the trips that did
 * succeed are cancelled and nothing is persisted, so the local order never
 * claims a state the real dispatch system doesn't have.
 *
 * Everything that touches the network or the database is injected, so the whole
 * flow is unit-testable without a live backend and stays easy to wrap in
 * integration tests later.
 */
import { createTripsForLegs, type PromoteTripRequest } from "@/lib/promote-order-trips";
import { shouldPromoteParent, type LegStatus } from "@/lib/confirm-plan";

/** Hard cap on any single external dispatch call. */
export const EXTERNAL_CALL_TIMEOUT_MS = 15_000;

/**
 * Rejects when `promise` hasn't settled within `ms`. A hung upstream call must
 * never hang the confirm action — the caller treats a timeout as a failure and
 * rolls back like any other.
 */
export async function withTimeout<T>(
  promise: Promise<T>,
  ms: number = EXTERNAL_CALL_TIMEOUT_MS,
  label = "External dispatch call",
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<never>((_, reject) => {
        timer = setTimeout(
          () => reject(new Error(`${label} timed out after ${Math.round(ms / 1000)}s`)),
          ms,
        );
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export interface ConfirmLegTarget {
  legId: string;
  legSequence: number;
  /**
   * Payload for the external trip, or null when this leg is local-only
   * (Manual / bobtail states that never create an external trip).
   */
  tripRequest: PromoteTripRequest | null;
}

export interface ConfirmExecutePorts {
  /** Issues one external create call. Returns the new trip id. */
  createTrip: (req: PromoteTripRequest) => Promise<string>;
  /** Cancels an external trip during rollback. */
  cancelTrip: (tripId: string, reason: string) => Promise<unknown>;
  /** Persists confirmed legs + their trip ids. Runs only after EAPI succeeds. */
  persistLegs: (
    legs: Array<{ legId: string; legSequence: number; eapiTripId: string | null }>,
  ) => Promise<void>;
  /** Promotes the parent order to `confirmed`. Called only when every leg is. */
  promoteParent: () => Promise<void>;
  /** Audit trail entry. Failures here are logged, never fatal. */
  logConfirmation?: (
    legs: Array<{ legId: string; legSequence: number; eapiTripId: string | null }>,
  ) => Promise<void>;
  /** Statuses of every leg on the order *after* this write, for the rollup. */
  legStatusesAfter: (confirmedLegIds: readonly string[]) => (LegStatus | string | null)[];
  timeoutMs?: number;
}

export interface ConfirmExecuteResult {
  confirmedLegIds: string[];
  createdTripIds: Array<{ legSequence: number; eapiTripId: string }>;
  parentPromoted: boolean;
}

/**
 * Confirms the given legs. A one-leg order is the n = 1 case of the exact same
 * fan-out, rollback and rollup as a five-leg order.
 */
export async function executeConfirm(
  targets: readonly ConfirmLegTarget[],
  ports: ConfirmExecutePorts,
): Promise<ConfirmExecuteResult> {
  if (targets.length === 0) {
    throw new Error("No legs were selected to confirm.");
  }

  const timeoutMs = ports.timeoutMs ?? EXTERNAL_CALL_TIMEOUT_MS;
  const requests = targets
    .map((t) => t.tripRequest)
    .filter((r): r is PromoteTripRequest => r !== null);

  // Step 1 — external system first.
  const created = await createTripsForLegs({
    requests,
    create: (req) => withTimeout(ports.createTrip(req), timeoutMs, `Create trip for leg ${req.legSequence}`),
    cancel: (tripId, reason) =>
      withTimeout(ports.cancelTrip(tripId, reason), timeoutMs, `Cancel trip ${tripId}`),
    rollbackReason: "Confirmation rollback",
    logLabel: "confirm",
  });

  const tripBySequence = new Map(created.map((c) => [c.legSequence, c.eapiTripId]));
  const persisted = targets.map((t) => ({
    legId: t.legId,
    legSequence: t.legSequence,
    eapiTripId: tripBySequence.get(t.legSequence) ?? null,
  }));

  // Step 2 — local write. A failure here rolls the external trips back so the
  // two systems can't diverge.
  try {
    await ports.persistLegs(persisted);
  } catch (err) {
    for (const c of created) {
      try {
        await withTimeout(
          ports.cancelTrip(c.eapiTripId, "Confirmation rollback (local write failed)"),
          timeoutMs,
          `Cancel trip ${c.eapiTripId}`,
        );
      } catch (rollbackErr: unknown) {
        console.warn("[confirm] rollback cancel failed", rollbackErr);
      }
    }
    throw err;
  }

  const confirmedLegIds = targets.map((t) => t.legId);

  let parentPromoted = false;
  if (shouldPromoteParent(ports.legStatusesAfter(confirmedLegIds))) {
    await ports.promoteParent();
    parentPromoted = true;
  }

  if (ports.logConfirmation) {
    try {
      await ports.logConfirmation(persisted);
    } catch (logErr: unknown) {
      console.warn("[confirm] audit log failed", logErr);
    }
  }

  return {
    confirmedLegIds,
    createdTripIds: created,
    parentPromoted,
  };
}
